import BaseApi from '@core/api/model';

export class HoldingApi extends BaseApi {
    static resource = 'holdings';

    get endpoints() {
        return {
            ...super.endpoints,
            getStatistics: this.getStatistics.bind(this),
            byCompany: this.byCompany.bind(this),
            byProduct: this.byProduct.bind(this),
            byTranche: this.byTranche.bind(this),
            byPrivateMarketStructure: this.byPrivateMarketStructure.bind(this),
            byPrivateMarketFund: this.byPrivateMarketFund.bind(this),
            investmentsByTranche: this.investmentsByTranche.bind(this),
            allot: this.allot.bind(this),
            merge: this.merge.bind(this)
        };
    }

    async getStatistics(params = {}) {
        return await BaseApi.api.get(`${this.base_url}/statistics`, BaseApi.removeNull(params));
    }

    async byCompany(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = BaseApi.extractFilter(filters, 'investor_id');

        let url = `${this.base_url}/summaries/company`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        const response = await this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });

        if (response && Array.isArray(response.data) && response.data.length > 0) {
            for (const item of response.data) {
                item.closed = item.is_closed;
            }
        }

        return response;
    }

    async byProduct(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let extract = BaseApi.extractFilter(filters, 'investor_id');

        let url = `${this.base_url}/summaries/product`;

        if (extract.value) {
            url += `/${extract.value}`;
        }

        const response = await this.index(offset, limit, sort_by, extract.filters, {
            ...config,
            base_url: url
        });

        if (response && Array.isArray(response.data) && response.data.length > 0) {
            const products = response.data.map(item => ({ id: item.product_id, name: item.name }));

            for (const item of response.data) {
                item.closed = item.share_quantity === 0 && !item.pending_allotment;
                if (item.merged && item.closed && item.related_product_id) {
                    const productId = item.related_product_id;
                    let relatedProduct = products.find(p => p.id === productId);

                    if (!relatedProduct) {
                        try {
                            const result = await this.byProduct(0, 1, null, { product_id: productId });
                            relatedProduct = result.data[0];
                        } catch (error) {
                            console.error(
                                `Error fetching related product for ${item.name} with id ${productId}:`,
                                error
                            );
                            continue;
                        }
                    }

                    item.related_product = relatedProduct;
                }
            }
        }

        return response;
    }

    async byTranche(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let investorExtract = BaseApi.extractFilter(filters, 'investor_id');
        let taxStatusExtract = BaseApi.extractFilter(investorExtract.filters, 'tax_status');

        let url = `${this.base_url}/summaries/tranche`;

        if (taxStatusExtract.value) {
            url += `/${taxStatusExtract.value}`;
        }

        if (investorExtract.value) {
            url += `/${investorExtract.value}`;
        }

        const response = await this.index(offset, limit, sort_by, taxStatusExtract.filters, {
            ...config,
            base_url: url
        });

        if (response && Array.isArray(response.data) && response.data.length > 0) {
            for (const item of response.data) {
                item.closed = item.is_closed;
            }
        }

        return response;
    }

    async byPrivateMarketStructure(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let holdingExtract = BaseApi.extractFilter(filters, 'holding_id');
        let structureExtract = BaseApi.extractFilter(holdingExtract.filters, 'structure');

        let url = `${this.base_url}/private-market`;

        if (structureExtract.value) {
            url += `/${structureExtract.value.toLowerCase()}`;

            if (holdingExtract.value) {
                url += `/${holdingExtract.value}`;
            }
        }

        const response = await this.index(offset, limit, sort_by, structureExtract.filters, {
            ...config,
            base_url: url
        });

        if (response && Array.isArray(response.data) && response.data.length > 0) {
            for (const item of response.data) {
                item.closed = item.is_closed;
            }
        }

        return response;
    }

    async byPrivateMarketFund(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        let trancheExtract = BaseApi.extractFilter(filters, 'tranche_id');

        let url = `${this.base_url}/private-market/fund`;

        if (trancheExtract.value) {
            url += `/${trancheExtract.value}`;
        }

        const response = await this.index(offset, limit, sort_by, trancheExtract.filters, {
            ...config,
            base_url: url
        });

        if (response && Array.isArray(response.data) && response.data.length > 0) {
            for (const item of response.data) {
                item.closed = item.is_closed;
            }
        }

        return response;
    }

    async investmentsByTranche(offset = null, limit = null, sort_by = null, filters = {}, config = {}) {
        return this.index(offset, limit, sort_by, filters, {
            ...config,
            base_url: `${this.base_url}/summaries/tranche`
        });
    }

    async allot(id, allotment = {}) {
        let response = await BaseApi.api.post(`${this.base_url}/${id}/allot`, allotment);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            this._updateItemInCollections(id, response.data);
            this._insertModel(response.data, this.ApiResponseFormatEnum.DETAIL);
        });
    }

    async merge(id, targetHoldingId = {}) {
        if (!id) {
            throw new Error('Holding ID is required for merging holdings.');
        }

        if (!targetHoldingId) {
            throw new Error('Target holding ID is required for merging holdings.');
        }

        let response = await BaseApi.api.post(`${this.base_url}/${id}/merge/${targetHoldingId}`);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }
}

export default HoldingApi;
